/**
 * The browser half of Web Push for staff booking alerts: registering the
 * service worker (`public/sw.js`), subscribing this device, and undoing it at
 * sign-out. No HTTP here — the server calls live in `store/action.js` — and no
 * React, so `logout()` can use it too.
 *
 * Why push and not just `new Notification()`: Chrome on Android refuses that
 * constructor outright ("use ServiceWorkerRegistration.showNotification()"),
 * and a phone suspends a backgrounded page's timers, so the in-tab polling
 * that came before reached nobody on a phone. A push is delivered by the
 * operating system to the service worker, with the app closed.
 */

const SERVICE_WORKER_URL = "/sw.js";

/**
 * `navigator.serviceWorker`, or null. Reading it throws in some sandboxed and
 * storage-blocked frames, and `logout()` must never throw.
 */
const serviceWorkers = () => {
  try {
    return typeof navigator !== "undefined" && "serviceWorker" in navigator
      ? navigator.serviceWorker
      : null;
  } catch {
    return null;
  }
};

/** Whether this browser can receive a push at all. iPhone: only once installed to the home screen. */
export const isPushSupported = () =>
  typeof window !== "undefined" &&
  "Notification" in window &&
  "PushManager" in window &&
  serviceWorkers() != null;

/** The VAPID key as `pushManager.subscribe` wants it. */
export const urlBase64ToUint8Array = (value) => {
  const padded = value + "=".repeat((4 - (value.length % 4)) % 4);
  const raw = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
};

/**
 * Whether an existing subscription was made with this key. A browser that
 * does not report the key gets the benefit of the doubt — resubscribing on
 * every load would mint a new endpoint each time.
 */
const madeWithKey = (subscription, key) => {
  const current = subscription.options?.applicationServerKey;
  if (!current) return true;

  const bytes = new Uint8Array(current);
  return bytes.length === key.length && bytes.every((byte, i) => byte === key[i]);
};

let registration = null;

/**
 * Registers the service worker once per page and resolves when it is active.
 * Called early — before the click that subscribes — because Safari on iPhone
 * only lets a page subscribe close to the tap that asked, and installing a
 * worker in between would use that window up.
 */
export const registerServiceWorker = () => {
  if (!registration) {
    const container = serviceWorkers();

    registration = container
      ? container.register(SERVICE_WORKER_URL).then(() => container.ready)
      : Promise.reject(new Error("Service workers are not available."));

    // Let a later call try again rather than inherit this failure for good.
    registration.catch(() => {
      registration = null;
    });
  }

  return registration;
};

/**
 * Subscribes this device with the server's public key, or reuses the
 * subscription it already has, and returns what the server stores:
 * `{ endpoint, p256dh, auth }`.
 */
export const subscribeDevice = async (publicKey) => {
  const worker = await registerServiceWorker();
  const key = urlBase64ToUint8Array(publicKey);

  let subscription = await worker.pushManager.getSubscription();

  if (subscription && !madeWithKey(subscription, key)) {
    // Made with a key the server has since rotated: nothing it sends could
    // reach this subscription any more.
    await subscription.unsubscribe();
    subscription = null;
  }

  if (!subscription) {
    subscription = await worker.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: key,
    });
  }

  const { endpoint, keys } = subscription.toJSON();
  return { endpoint, p256dh: keys?.p256dh, auth: keys?.auth };
};

/**
 * Ends this device's subscription, at sign-out. The push service then answers
 * the server with 410 and the server forgets the device; no request of ours
 * is needed, which matters because the token is already gone by now.
 * Best-effort and silent — signing out must never wait on it or fail by it.
 */
export const forgetPushSubscription = () => {
  const container = serviceWorkers();
  if (!container) return Promise.resolve();

  return container
    .getRegistration()
    .then((worker) => worker?.pushManager?.getSubscription())
    .then((subscription) => subscription?.unsubscribe())
    .catch(() => {});
};

/**
 * Shows an alert from the page itself — the fallback for browsers without
 * push. Through the service worker when there is one, since Chrome on Android
 * refuses `new Notification()`; the constructor elsewhere.
 */
export const showLocalNotification = async (title, options) => {
  try {
    const container = serviceWorkers();
    const worker = container ? await container.getRegistration() : null;

    if (worker) {
      await worker.showNotification(title, options);
      return;
    }

    new Notification(title, options);
  } catch {
    // Nothing to recover — the dashboard's bookings list is still the truth.
  }
};
